import { useEffect, useState } from 'react';
import { createApiKey, deleteApiKey, getApiKeyUsage, getCurrentUser, listApiKeys } from '../api/client';
import { formatAppError } from '../utils/errorMessage';

interface ApiKeyRecord {
  id: number;
  public_id: string;
  tier_id: number | null;
  created_at: string;
  expires_at?: string | null;
  is_active: boolean;
}

interface ApiKeyUsage {
  monthly_count: number;
  monthly_limit: number | null;
  remaining: number | null;
  reset_at: string;
  plan_name?: string;
}

export default function ApiKeys() {
  const [apiKeys, setApiKeys] = useState<ApiKeyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<string | null>(null);
  const [hasActiveKey, setHasActiveKey] = useState(false);
  const [usageByPublicId, setUsageByPublicId] = useState<Record<string, ApiKeyUsage>>({});
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  useEffect(() => {
    void fetchApiKeys();
    void fetchCurrentPlan();

    const handleAuthStateChanged = () => {
      void fetchCurrentPlan();
      void fetchApiKeys();
    };
    const handleFocus = () => {
      void fetchCurrentPlan();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void fetchCurrentPlan();
      }
    };

    window.addEventListener('authStateChanged', handleAuthStateChanged);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('authStateChanged', handleAuthStateChanged);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const response = await listApiKeys();
      const keys = response.data || [];
      setApiKeys(keys);
      setHasActiveKey(keys.some((key: ApiKeyRecord) => key.is_active));

      const usageResponses = await Promise.all(
        keys.map(async (key: ApiKeyRecord) => {
          try {
            const usageResponse = await getApiKeyUsage(key.public_id);
            return [key.public_id, usageResponse.data] as const;
          } catch {
            return [
              key.public_id,
              {
                monthly_count: 0,
                monthly_limit: null,
                remaining: null,
                reset_at: '',
              },
            ] as const;
          }
        })
      );

      const usageMap = Object.fromEntries(usageResponses) as Record<string, ApiKeyUsage>;
      setUsageByPublicId(usageMap);

      const usagePlan =
        keys
          .map((key: ApiKeyRecord) => usageMap[key.public_id]?.plan_name)
          .find((planName: string | undefined) => typeof planName === 'string' && planName.length > 0) ?? null;
      if (usagePlan) {
        setCurrentPlan(usagePlan);
      }
    } catch (err: any) {
      const formatted = formatAppError(err, 'api-keys-list', 'Failed to load API keys.');
      setError(formatted.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentPlan = async () => {
    try {
      const response = await getCurrentUser();
      const planName = response.data?.plan_name ?? 'free';
      setCurrentPlan(planName);
    } catch (err) {
      // Keep previous value to avoid false "free" flashes on transient errors.
    }
  };

  const displayPlan = (currentPlan ?? 'free').toLowerCase();
  const displayPlanLabel = displayPlan.charAt(0).toUpperCase() + displayPlan.slice(1);

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    setCreatedSecret(null);
    try {
      const response = await createApiKey();
      setCreatedSecret(response.data.key);
      await fetchApiKeys();
    } catch (err: any) {
      const formatted = formatAppError(err, 'api-keys-create', 'Failed to create API key.');
      setError(formatted.message);
    } finally {
      setCreating(false);
    }
  };

  const activeUsage = (() => {
    const activeKey = apiKeys.find((key) => key.is_active);
    if (!activeKey) return null;

    const usage = usageByPublicId[activeKey.public_id];
    if (!usage) return null;

    return {
      publicId: activeKey.public_id,
      plan: currentPlan,
      used: usage.monthly_count ?? 0,
      limit: usage.monthly_limit,
      remaining: usage.remaining,
      resetAt: usage.reset_at,
    };
  })();

  const handleRevokeClick = (publicId: string) => {
    setConfirmRevokeId(publicId);
  };

  const handleConfirmRevoke = async (publicId: string) => {
    setConfirmRevokeId(null);
    try {
      await deleteApiKey(publicId);
      await fetchApiKeys();
    } catch (err: any) {
      const formatted = formatAppError(err, 'api-keys-delete', 'Failed to revoke API key.');
      setError(formatted.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
      <div className="max-w-5xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-100 mb-2">
            API Keys
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Create and manage API keys for external integrations.
          </p>
          <div className="mt-3 flex justify-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-indigo-500/20 px-4 py-1 text-sm font-semibold text-indigo-300 border border-indigo-500/30">
              Current plan: {displayPlanLabel}
            </span>
          </div>
          {activeUsage && (
            <div className="mt-4 flex justify-center">
              <span className="inline-flex items-center gap-2 rounded-full bg-slate-800 px-4 py-1 text-sm font-medium text-slate-100 border border-slate-700">
                Used {activeUsage.used} / {activeUsage.limit ?? 'N/A'} · Remaining {activeUsage.remaining ?? 'N/A'}
                {activeUsage.resetAt ? ` · Resets ${new Date(activeUsage.resetAt).toLocaleString()}` : ''}
              </span>
            </div>
          )}
        </div>

        <div className="bg-slate-900/80 backdrop-blur-sm shadow-xl rounded-2xl border border-slate-700 p-6 sm:p-8 mb-8">
          <h2 className="text-xl font-semibold text-slate-100 mb-4">Create New API Key</h2>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 text-sm text-slate-300 bg-slate-800 border border-slate-700 rounded-xl px-4 py-3">
              One active API key is allowed per account.
            </div>
            <button
              onClick={handleCreate}
              disabled={creating || hasActiveKey}
              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create Key'}
            </button>
          </div>
          {hasActiveKey && (
            <div className="mt-3 text-sm text-slate-400">
              You already have an active API key. Revoke it to create a new one.
            </div>
          )}
          <div className="mt-2 text-xs text-slate-500">
            Note: API key secrets are shown only once when created.
          </div>

          {createdSecret && (
            <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
              <p className="text-sm text-green-300 font-semibold mb-1">New API Key (secret)</p>
              <p className="text-xs text-green-200 mb-2">
                This is the only time the secret will be shown. Save it now.
              </p>
              <div className="flex items-start gap-2">
                <div className="flex-1 font-mono text-sm text-slate-100 bg-slate-900 border border-slate-700 rounded-lg p-3 break-all">
                  {createdSecret}
                </div>
                <button
                  type="button"
                  onClick={() => copyToClipboard(createdSecret, 'secret')}
                  className="px-3 py-2 text-xs font-semibold rounded-lg bg-slate-800 text-slate-100 border border-slate-600 hover:bg-slate-700 min-w-[64px]"
                >
                  {copiedId === 'secret' ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-300">
              {error}
            </div>
          )}
        </div>

        <div className="bg-slate-900/80 backdrop-blur-sm shadow-xl rounded-2xl border border-slate-700 overflow-hidden">
          <div className="p-6 sm:p-8">
            <h2 className="text-xl font-semibold text-slate-100 mb-4">Your API Keys</h2>

            {loading ? (
              <div className="text-slate-400">Loading...</div>
            ) : apiKeys.length === 0 ? (
              <div className="text-slate-400">No API keys yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-700">
                  <thead className="bg-slate-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                        Public ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                        Tier ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                        Usage
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                        Resets
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-slate-900 divide-y divide-slate-700">
                    {apiKeys.map((key) => (
                      <tr key={key.public_id} className="hover:bg-slate-800/50 transition-colors duration-150">
                        <td className="px-4 py-3 text-sm font-mono text-slate-200">
                          {key.public_id}
                          <button
                            onClick={() => copyToClipboard(key.public_id, `pid-${key.public_id}`)}
                            className="ml-2 text-xs text-indigo-400 hover:text-indigo-300"
                          >
                            {copiedId === `pid-${key.public_id}` ? '✓ Copied!' : 'Copy'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-300">{key.tier_id ?? '—'}</td>
                        <td className="px-4 py-3 text-sm text-slate-300">
                          {new Date(key.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {key.is_active ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-300">
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-700 text-slate-300">
                              Inactive
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-300">
                          {usageByPublicId[key.public_id]
                            ? (
                                <>
                                  {usageByPublicId[key.public_id].monthly_count}
                                  {' / '}
                                  {usageByPublicId[key.public_id].monthly_limit ?? 'N/A'}
                                  {usageByPublicId[key.public_id].remaining !== null && (
                                    <span className="block text-xs text-slate-400">
                                      Remaining: {usageByPublicId[key.public_id].remaining}
                                    </span>
                                  )}
                                </>
                              )
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-300">
                          {usageByPublicId[key.public_id]?.reset_at
                            ? new Date(usageByPublicId[key.public_id].reset_at).toLocaleString()
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {confirmRevokeId === key.public_id ? (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-400">Sure?</span>
                              <button
                                onClick={() => handleConfirmRevoke(key.public_id)}
                                className="text-xs px-2 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors"
                              >
                                Yes, revoke
                              </button>
                              <button
                                onClick={() => setConfirmRevokeId(null)}
                                className="text-xs px-2 py-1 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 font-medium transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleRevokeClick(key.public_id)}
                              className="text-red-400 hover:text-red-300 font-medium"
                            >
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
